import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import get from 'lodash/get'
import numeral from 'numeral'
import { FC, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Icons, PointDisplay, StoreItemImageArea } from 'utoniq-ui'

import {
  ItemType,
  SalesCondition,
  SalesStatus,
  StoreItemFragment,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { App } from '@/core/native'
import { useAnalytics } from '@/lib/analytics'

enum Size {
  SMALL = 'small',
  MEDIUM = 'medium',
}

enum Orientation {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export type StoreItemAreaProps = {
  canGoBack?: boolean
  artistId: string
  item: StoreItemFragment
  size?: `${Size}`
  orientation?: `${Orientation}`
  /** analyticsのview_itemイベントを送信するかどうか */
  trackView?: boolean
  onClick?: () => void
  collectionTitle?: string
  collectionId?: string
  eventId?: string
  showMenberOnlyIcon?: boolean
}

export const StoreItemArea: FC<StoreItemAreaProps> = ({
  canGoBack,
  artistId,
  item,
  size = Size.MEDIUM,
  orientation = Orientation.VERTICAL,
  trackView = false,
  onClick,
  collectionTitle,
  eventId,
  collectionId,
  showMenberOnlyIcon,
}) => {
  const { t } = useTranslation('artistStore')
  const { generatePath, routerPages } = useRouter()
  const { analytics } = useAnalytics()
  // アイテムの値段表記
  const formattedPrice = useMemo(() => {
    // SKUがなければ、そのままフォーマットして返す
    if (!item.skuSettings) return numeral(item.price).format('0,0')

    // SKUがある場合、最小の値段をフォーマットして返す
    const prices = item.skuSettings.units.map(unit => unit.price ?? 0)
    const isAllSame = prices.every(price => price === prices[0])

    if (isAllSame)
      return numeral(prices[0] ? prices[0] : item.price).format('0,0')

    const minPrice = Math.min(...prices)

    return `${numeral(minPrice).format('0,0')}〜`
  }, [item])
  const isNft = !!item?.nft?.chains

  const remainingNumber =
    typeof item.issuedNumber === 'number'
      ? item.issuedNumber -
        item.purchasedNumber -
        (item.pendingPurchaseNumber ?? 0)
      : null

  const nftIconSize = {
    small: 'small',
    medium: 'large',
  } as const
  const itemIcon = useMemo(() => {
    switch (item?.type) {
      case ItemType.Audio:
        return <Icons.Audio fontSize="xSmall" />
      case ItemType.Video:
        return <Icons.Video fontSize="xSmall" />
      case ItemType.Magazine:
        return <Icons.Magazine fontSize="xSmall" />
      case ItemType.Merch:
        return <Icons.Merch fontSize="xSmall" />
      case ItemType.Wallpaper:
        return <Icons.Wallpaper fontSize="xSmall" />
      case ItemType.Ticket:
        return <Icons.Ticket fontSize="xSmall" />
      default:
        return null
    }
  }, [item?.type])

  const isResaleAvailable = useMemo<boolean>(() => {
    //リセールを許可していないもの
    if (!item.resaleEnabled) {
      return false
    }

    //非限定アイテムであるもの
    const issuedNumber = Number(item.issuedNumber) || null
    if (issuedNumber === null) {
      return false
    }

    //リセールを許可しているが、完売していないもの
    const purchasedNumber = Number(item.purchasedNumber) || 0
    const pendingPurchaseNumber = Number(item.pendingPurchaseNumber) || 0
    if (issuedNumber > purchasedNumber + pendingPurchaseNumber) {
      return false
    }

    // リセールを許可しており、完売しているが、リセール期間中でないもの
    const now = dayjs().unix()
    const startedAt = get(
      item,
      'metadata.ticket.resalePeriod.startedAt._seconds'
    )
    const endedAt = get(item, 'metadata.ticket.resalePeriod.endedAt._seconds')
    if (
      !startedAt ||
      !endedAt ||
      dayjs(startedAt * 1000).unix() > now ||
      dayjs(endedAt * 1000).unix() < now
    ) {
      return false
    }
    return true
  }, [item])

  useEffect(() => {
    if (!item || !trackView) return
    analytics?.track('view_item', {
      currency: 'JPY',
      value: item.price,
      items: [
        {
          item_id: item.id,
          item_name: item.title,
          item_category: 'store_item',
          item_category2: item.type,
          item_category3: App.getPlatform(),
          item_category4: 1,
          item_category5: item.issuedArtist?.id,
          price: item.price,
        },
      ],
    })
  }, [item, analytics])

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: 'unset',
        mb: size === Size.MEDIUM ? 4 : 0,
        borderRadius: 0,
      }}
    >
      <CardActionArea
        component={Link}
        onClick={onClick}
        to={{
          pathname: generatePath(routerPages.ArtistStoreItemDetail, {
            artistId,
            itemId: item.id ?? '#',
          }),
          state: { canGoBack: true },
        }}
        sx={{
          maxWidth: size === Size.MEDIUM ? 108 : 80,
        }}
      >
        <Stack alignItems="center" sx={{ position: 'relative' }}>
          {isNft && (
            <Icons.Nft
              fontSize={nftIconSize[size]}
              filledColor
              sx={{ position: 'absolute', right: 0, bottom: 4, zIndex: 1 }}
            />
          )}
          {isResaleAvailable && (
            <Box
              sx={{
                position: 'absolute',
                inset: '1px auto auto 0',
                py: '2px',
                px: 1,
                zIndex: 1,
                bgcolor: 'rgba(0, 0, 0, 0.8)',
              }}
            >
              <Typography component="p" variant="overline" color="white">
                {t('resale')}
              </Typography>
            </Box>
          )}
          <StoreItemImageArea
            src={
              item.thumbnail.front?.mediumUri ?? item.thumbnail.front?.uri ?? ''
            }
            size={size}
          />
          {size === Size.MEDIUM &&
            ((item?.isDownloadable ||
              item.metadata?.wallpaper?.isPrintEnabled) &&
            item.type === ItemType.Wallpaper ? (
              <Stack
                direction="row"
                spacing={'2px'}
                sx={{
                  position: 'absolute',
                  top: '6px',
                  left: '6px',
                  zIndex: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 100,
                  height: 20,
                  width:
                    item?.isDownloadable &&
                    item.metadata?.wallpaper?.isPrintEnabled
                      ? 42
                      : 20,
                  p: 1,
                }}
                alignItems="center"
                justifyContent="center"
              >
                {item?.isDownloadable && (
                  <Icons.WallpaperDownload
                    sx={{ color: 'grey.700' }}
                    fontSize="xSmall"
                  />
                )}
                {item.metadata?.wallpaper?.isPrintEnabled && (
                  <Icons.WallpaperPrinter
                    sx={{ color: 'grey.700' }}
                    fontSize="xSmall"
                  />
                )}
              </Stack>
            ) : (
              <Box
                sx={{
                  borderRadius: '100%',
                  bgcolor: 'grey.50',
                  width: '20px',
                  height: '20px',
                  position: 'absolute',
                  top: '6px',
                  left: '6px',
                  zIndex: 1,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {itemIcon}
              </Box>
            ))}
        </Stack>
        <CardContent
          sx={{
            pt: size === Size.MEDIUM ? 2 : 0,
            px: 0,
            pb: 0,
          }}
        >
          <Stack spacing={1}>
            {!!collectionTitle && (
              <Typography
                variant="overline"
                color="text.secondary"
                sx={styleSets.lineCamp(2)}
              >
                {collectionTitle}
              </Typography>
            )}
            <Stack direction="row" alignItems="center" spacing={1}>
              {size === Size.MEDIUM && (
                <Typography variant="subtitle2" sx={styleSets.lineCamp(2)}>
                  {item.title}
                </Typography>
              )}
              <Box flexGrow={1} />
              <Stack direction="row" spacing={1}>
                {showMenberOnlyIcon && item.isPremium && (
                  <Icons.MembersOnly
                    fontSize="small"
                    sx={{ color: 'info.main', height: 12, width: 12, ml: 1 }}
                  />
                )}
              </Stack>
            </Stack>
            {item.price ? (
              <PointDisplay
                point={formattedPrice}
                size={size}
                paymentType={item.paymentType ?? 'prepaid_point'}
              />
            ) : (
              <Typography
                variant="overline"
                color="primary"
                sx={{
                  pt: size === Size.SMALL ? '5px' : 0,
                }}
              >
                {t('free', '無料')}
              </Typography>
            )}
            {size === Size.MEDIUM && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  height: 16,
                }}
              >
                <SalesStatusCell
                  item={item}
                  remainingNumber={remainingNumber}
                />
              </Stack>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

// アイテムの在庫状況を表示する
const SalesStatusCell: FC<{
  item: StoreItemFragment
  remainingNumber: number | null
}> = ({ item, remainingNumber }) => {
  const { t } = useTranslation('artistStore')

  if (item.salesStatus === SalesStatus.Stopped) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t('stopped', '販売停止')}
      </Typography>
    )
  }
  if (item.salesCondition === SalesCondition.Before) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t('beforeSelling', '販売開始前')}
      </Typography>
    )
  }
  if (item.salesCondition === SalesCondition.End) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t('endSelling', '販売終了')}
      </Typography>
    )
  }
  if (!!item.issuedNumber && remainingNumber === 0) {
    return (
      <Typography variant="caption" color="error.main">
        {t('soldout')}
      </Typography>
    )
  }
  if (!item.hideSalesNumber && item.issuedNumber && !!remainingNumber) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t('remaining', '残り')}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: remainingNumber > 9 ? 'text.primary' : 'error.main',
          }}
        >
          {`${remainingNumber}/${item.issuedNumber || 0}`}
        </Typography>
      </Box>
    )
  }
  return null
}
